import { useCallback, useEffect, useState } from 'react';

import { ICollaboratorMembershipData } from '../../interfaces/collaborators';
import Companies from '../../services/api/collaborators/collaborators';
import { useIsMounted } from '../useIsMounted';

interface ICollaboratorMembershipDataState {
  data: ICollaboratorMembershipData | undefined;
  isLoading: boolean;
  error: boolean;
}

const useCollaboratorMembershipData = (companyId: string, email: string, enabled: boolean) => {
  const [membershipDataState, setMembershipDataState] = useState<ICollaboratorMembershipDataState>({
    data: undefined,
    isLoading: enabled,
    error: false
  });

  const isMounted = useIsMounted();

  const fetchData = useCallback(async () => {
    setMembershipDataState((prevState) => ({ ...prevState, isLoading: true, error: false }));

    let response: ICollaboratorMembershipData | undefined = undefined;
    let hasError = false;

    try {
      response = await Companies.getCollaboratorMembershipData(email, companyId);
    } catch {
      hasError = true;
    } finally {
      if (isMounted) {
        setMembershipDataState((prevState) => ({
          ...prevState,
          isLoading: false,
          error: hasError,
          data: hasError ? prevState.data : response
        }));
      }
    }
  }, [companyId, email, isMounted]);

  useEffect(() => {
    if (!enabled) {
      setMembershipDataState({ data: undefined, isLoading: false, error: false });
      return;
    }
    void fetchData();
  }, [fetchData, enabled]);

  return { membershipDataState, refetchMembershipData: fetchData };
};

export default useCollaboratorMembershipData;
